import React from 'react';
import {
  Button,
  Box,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  HStack,
  VStack,
  Divider,
} from '@chakra-ui/react';
import { useBlogTranslation } from '../../hooks/useBlogTranslation';
import { FaLanguage, FaCheck, FaExclamationTriangle } from 'react-icons/fa';

interface UniversalTranslatorProps {
  title: string;
  content: string;
  onTranslationComplete: (translatedTitle: string, translatedContent: string) => void;
  disabled?: boolean;
  contentType?: 'article' | 'activity' | 'page' | 'sponsor' | 'player';
}

export const UniversalTranslator: React.FC<UniversalTranslatorProps> = ({
  title,
  content,
  onTranslationComplete,
  disabled = false,
  contentType = 'article',
}) => {
  const { translateBlog, isTranslating, translationError, detectSourceLanguage, getTargetLanguage } = useBlogTranslation();

  const handleTranslate = async () => {
    try {
      const result = await translateBlog(title, content);
      onTranslationComplete(result.title, result.content);
    } catch (error) {
      // Error is handled by the hook
    }
  };

  const sourceLang = detectSourceLanguage(title + ' ' + content);
  const targetLang = getTargetLanguage();
  const shouldTranslate = sourceLang !== targetLang && title && content;

  if (!shouldTranslate) {
    return (
      <Alert status="info" borderRadius="md">
        <AlertIcon as={FaLanguage} />
        <Box>
          <AlertTitle>Translation not needed</AlertTitle>
          <AlertDescription>
            Content is already in {sourceLang === 'cs' ? 'Czech' : 'English'} or the target language is the same.
          </AlertDescription>
        </Box>
      </Alert>
    );
  }

  const getContentTypeLabel = () => {
    switch (contentType) {
      case 'activity': return 'Activity';
      case 'page': return 'Page';
      case 'sponsor': return 'Sponsor';
      case 'player': return 'Player';
      default: return 'Content';
    }
  };

  return (
    <VStack spacing={3} align="stretch">
      <Box>
        <HStack spacing={2} mb={2}>
          <FaLanguage />
          <Text fontWeight="medium">
            Translate {getContentTypeLabel()} from {sourceLang === 'cs' ? 'Czech' : 'English'} to {targetLang === 'cs' ? 'Czech' : 'English'}
          </Text>
        </HStack>
        
        <Button
          colorScheme="blue"
          onClick={handleTranslate}
          isLoading={isTranslating}
          loadingText="Translating..."
          leftIcon={<FaLanguage />}
          disabled={disabled || isTranslating}
          size="sm"
        >
          {isTranslating ? 'Translating...' : `Translate ${getContentTypeLabel()}`}
        </Button>
      </Box>

      {translationError && (
        <Alert status="error" borderRadius="md">
          <AlertIcon as={FaExclamationTriangle} />
          <Box>
            <AlertTitle>Translation Failed</AlertTitle>
            <AlertDescription>{translationError}</AlertDescription>
          </Box>
        </Alert>
      )}

      {!isTranslating && !translationError && (
        <Alert status="success" borderRadius="md">
          <AlertIcon as={FaCheck} />
          <Box>
            <AlertTitle>Ready to Translate</AlertTitle>
            <AlertDescription>
              Click the translate button to convert this {getContentTypeLabel().toLowerCase()} to {targetLang === 'cs' ? 'Czech' : 'English'}.
            </AlertDescription>
          </Box>
        </Alert>
      )}

      <Divider />
    </VStack>
  );
};
